/**
 * EvidencePanel — collapsible panel showing clinical evidence for a suggestion.
 * Shows source document, excerpt with highlights, key findings, and relevance score.
 */
import type { Evidence, DocumentType } from '../types';

interface Props {
  evidence: Evidence[];
  clinicalReasoning: string;
}

function docTypeLabel(dt: DocumentType): string {
  const map: Record<DocumentType, string> = {
    lab_result: 'Lab Result',
    progress_note: 'Progress Note',
    consult: 'Consult Note',
    medication_list: 'Medication List',
    procedure_report: 'Procedure Report',
    vital_signs: 'Vital Signs',
    imaging: 'Imaging',
    discharge_summary: 'Discharge Summary',
    other: 'Document',
  };
  return map[dt] ?? 'Document';
}

function docTypeIcon(dt: DocumentType): string {
  const map: Record<DocumentType, string> = {
    lab_result: '🧪',
    progress_note: '📝',
    consult: '👨‍⚕️',
    medication_list: '💊',
    procedure_report: '🔬',
    vital_signs: '📊',
    imaging: '🩻',
    discharge_summary: '🏥',
    other: '📄',
  };
  return map[dt] ?? '📄';
}


function HighlightedExcerpt({ excerpt, highlights }: { excerpt: string; highlights: Evidence['highlights'] }) {
  if (!highlights || highlights.length === 0) {
    return <p className="text-xs text-gray-700 leading-relaxed">{excerpt}</p>;
  }

  // Use text-based search instead of start/end offsets (offsets are unreliable)
  const ranges: { start: number; end: number }[] = [];
  for (const hl of highlights) {
    const idx = excerpt.indexOf(hl.text);
    if (idx !== -1) {
      ranges.push({ start: idx, end: idx + hl.text.length });
    }
  }

  if (ranges.length === 0) {
    return <p className="text-xs text-gray-700 leading-relaxed font-mono bg-gray-50 p-2 rounded border border-gray-200">{excerpt}</p>;
  }

  // Sort and merge overlapping ranges
  ranges.sort((a, b) => a.start - b.start);
  const merged: { start: number; end: number }[] = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const prev = merged[merged.length - 1];
    if (ranges[i].start <= prev.end) {
      prev.end = Math.max(prev.end, ranges[i].end);
    } else {
      merged.push(ranges[i]);
    }
  }

  const segments: { text: string; highlighted: boolean }[] = [];
  let pos = 0;
  for (const r of merged) {
    if (r.start > pos) {
      segments.push({ text: excerpt.slice(pos, r.start), highlighted: false });
    }
    segments.push({ text: excerpt.slice(r.start, r.end), highlighted: true });
    pos = r.end;
  }
  if (pos < excerpt.length) {
    segments.push({ text: excerpt.slice(pos), highlighted: false });
  }

  return (
    <p className="text-xs text-gray-700 leading-relaxed font-mono bg-gray-50 p-2 rounded border border-gray-200">
      {segments.map((seg, i) =>
        seg.highlighted ? (
          <mark key={i} className="bg-yellow-200 text-yellow-900 font-semibold rounded px-0.5">
            {seg.text}
          </mark>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </p>
  );
}

export function EvidencePanel({ evidence, clinicalReasoning }: Props) {
  return (
    <div className="border-t border-gray-200 bg-gray-50 animate-fade-in">
      {/* Clinical reasoning */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-start gap-2">
          <span className="flex-shrink-0 mt-0.5 text-base">🧠</span>
          <div>
            <p className="text-2xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Justification
            </p>
            <p className="text-xs text-gray-700 leading-relaxed">{clinicalReasoning}</p>
          </div>
        </div>
      </div>

      {/* Evidence cards */}
      <div className="px-4 pb-4 space-y-3">
        <p className="text-2xs font-semibold text-gray-500 uppercase tracking-wide mt-1">
          Supporting Evidence ({evidence.length})
        </p>
        {evidence.map((ev) => (
          <div
            key={ev.id}
            className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm"
          >
            {/* Source header */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-base flex-shrink-0">{docTypeIcon(ev.documentType)}</span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">
                    {ev.sourceDocument}
                  </p>
                  <p className="text-2xs text-gray-400">
                    {docTypeLabel(ev.documentType)} ·{' '}
                    {new Date(ev.documentDate + 'T00:00:00').toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Key findings */}
            {ev.keyFindings.length > 0 && (
              <div className="mt-2 space-y-1">
                {ev.keyFindings.map((f, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <span className="text-emerald-500 text-xs flex-shrink-0 mt-0.5">✓</span>
                    <span className="text-xs text-gray-700">{f}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Excerpt */}
            <div className="mt-2">
              <p className="text-2xs text-gray-400 mb-1">Excerpt:</p>
              <HighlightedExcerpt excerpt={ev.excerpt} highlights={ev.highlights} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
